import {
  Badge,
  Button,
  HStack,
  SimpleGrid,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import { LuCalendar, LuSlidersHorizontal } from "react-icons/lu";
import { DatePicker } from "@/ui/datePickers/DatePicker";
import { Select } from "@/ui/selects/Select";
import { Field } from "@/ui/chakra/field";
import {
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverRoot,
  PopoverTrigger,
} from "@/ui/chakra/popover";
import {
  countActiveFilters,
  getDateRange,
  getOption,
  isFilterActive,
  type DateRangeValue,
  type FilterField,
  type FilterValues,
} from "./filterValues";

const DATE_PRESETS: { label: string; range: () => DateRangeValue }[] = [
  { label: "Hoy", range: () => ({ from: new Date(), to: new Date() }) },
  {
    label: "Últimos 7 días",
    range: () => ({ from: subDays(new Date(), 6), to: new Date() }),
  },
  {
    label: "Este mes",
    range: () => ({ from: startOfMonth(new Date()), to: new Date() }),
  },
  {
    label: "Mes anterior",
    range: () => {
      const previous = subMonths(new Date(), 1);
      return { from: startOfMonth(previous), to: endOfMonth(previous) };
    },
  },
];

const formatShort = (date: Date) => format(date, "dd/MM/yyyy");

const describeDateRange = ({ from, to }: DateRangeValue) => {
  if (from && to) {
    return from.toDateString() === to.toDateString()
      ? formatShort(from)
      : `${formatShort(from)} – ${formatShort(to)}`;
  }
  if (from) return `Desde ${formatShort(from)}`;
  if (to) return `Hasta ${formatShort(to)}`;
  return "";
};

interface TableFiltersProps {
  fields: FilterField[];
  value: FilterValues;
  onChange: (value: FilterValues) => void;
}

export const TableFiltersPopover = ({
  fields,
  value,
  onChange,
}: TableFiltersProps) => {
  const activeCount = countActiveFilters(value);
  const set = (key: string, fieldValue: FilterValues[string]) =>
    onChange({ ...value, [key]: fieldValue });

  return (
    <PopoverRoot positioning={{ placement: "bottom-start" }} lazyMount>
      <PopoverTrigger asChild>
        <Button
          size="xs"
          rounded="full"
          px={3}
          variant={activeCount ? "subtle" : "outline"}
          colorPalette={activeCount ? "brand" : "gray"}
        >
          <LuSlidersHorizontal />
          Más filtros
          {activeCount > 0 && (
            <Badge
              size="xs"
              rounded="full"
              variant="solid"
              colorPalette="brand"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent w={{ base: "calc(100vw - 32px)", sm: "420px" }}>
        <PopoverArrow />
        <PopoverBody>
          <Stack gap={4}>
            {fields.map((field) => {
              if (field.type === "dateRange") {
                const range = getDateRange(value, field.key) ?? {};
                return (
                  <Stack key={field.key} gap={2}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {field.label}
                    </Text>
                    <HStack gap={1.5} flexWrap="wrap">
                      {DATE_PRESETS.map((preset) => (
                        <Button
                          key={preset.label}
                          size="xs"
                          variant="outline"
                          onClick={() => set(field.key, preset.range())}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </HStack>
                    <SimpleGrid columns={2} gap={3}>
                      <Field label="Desde">
                        <DatePicker
                          value={range.from ?? null}
                          maxDate={range.to ?? null}
                          placeHolder="dd/mm/aaaa"
                          onChange={(from) =>
                            set(field.key, { ...range, from })
                          }
                        />
                      </Field>
                      <Field label="Hasta">
                        <DatePicker
                          value={range.to ?? null}
                          minDate={range.from ?? null}
                          placeHolder="dd/mm/aaaa"
                          onChange={(to) => set(field.key, { ...range, to })}
                        />
                      </Field>
                    </SimpleGrid>
                  </Stack>
                );
              }
              return (
                <Field key={field.key} label={field.label}>
                  <Select
                    isClearable
                    placeholder={field.placeholder ?? "Todos"}
                    value={getOption(value, field.key)}
                    onChange={(option) => set(field.key, option ?? undefined)}
                    options={field.options}
                  />
                </Field>
              );
            })}
          </Stack>
        </PopoverBody>
        <PopoverFooter justifyContent="flex-end">
          <Button
            size="sm"
            variant="ghost"
            disabled={!activeCount}
            onClick={() => onChange({})}
          >
            Limpiar filtros
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </PopoverRoot>
  );
};

export const ActiveFilterTags = ({
  fields,
  value,
  onChange,
}: TableFiltersProps) => {
  const active = fields.filter((field) => isFilterActive(value[field.key]));
  if (!active.length) return null;

  return (
    <HStack gap={1.5} flexWrap="wrap">
      {active.map((field) => {
        const label =
          field.type === "dateRange"
            ? describeDateRange(getDateRange(value, field.key) ?? {})
            : `${field.tagLabel ?? field.label}: ${getOption(value, field.key)?.label}`;
        return (
          <Tag.Root
            key={field.key}
            size="md"
            rounded="full"
            variant="subtle"
            colorPalette="brand"
            maxW="260px"
          >
            {field.type === "dateRange" && (
              <Tag.StartElement>
                <LuCalendar />
              </Tag.StartElement>
            )}
            <Tag.Label>{label}</Tag.Label>
            <Tag.EndElement>
              <Tag.CloseTrigger
                aria-label={`Quitar filtro ${label}`}
                onClick={() => onChange({ ...value, [field.key]: undefined })}
              />
            </Tag.EndElement>
          </Tag.Root>
        );
      })}
    </HStack>
  );
};

export const TableFilters = (props: TableFiltersProps) => (
  <>
    <TableFiltersPopover {...props} />
    <ActiveFilterTags {...props} />
  </>
);
